// FROM: https://github.com/modusign/modusign-client/blob/1f2cc00e317b3cc6ddf42ace319c72036470a423/app/src/components/UniversalCheckboxField/index.jsx

import { Box } from '@modusign/design-system';
import cx from 'classnames';
import { ChangeEvent, Component, ReactNode } from 'react';
import OutsideClickHandler from 'react-outside-click-handler';

import { CheckIcon } from '@app/components/Icons';
import { FieldBase } from '@app/features/apply-react-signing/components/FieldRenderer/components/FieldBase';
import { createAbsoluteNumber } from '@app/features/apply-react-signing/components/FieldRenderer/libs/createAbsoluteNumber';
import { createAbsoluteSize } from '@app/features/apply-react-signing/components/FieldRenderer/libs/createAbsoluteSize';
import { PointDelta } from '@app/features/apply-react-signing/components/FieldRenderer/libs/PointDelta';
import {
  AbsoluteNumber,
  AbsolutePoint,
  AbsoluteSize,
} from '@app/features/apply-react-signing/components/FieldRenderer/types';

import './UniversalCheckboxField.scss';

export interface UniversalCheckboxFieldProps {
  viewport: AbsoluteSize;
  point: AbsolutePoint;
  size: AbsoluteNumber;
  check: boolean;

  dragCancelSelectors: string[];
  draggable?: boolean;
  checkable?: boolean;
  resizable?: boolean;

  rootClassName?: string;
  boxClassName?: string;
  boxStyle?: React.CSSProperties;
  checkColor?: string;
  checkOpacity?: number;
  children: ReactNode;

  onBlur: () => void;
  onChangeCheck: (check: boolean) => void;
  onChangePoint: (point: AbsolutePoint) => void;
  onChangeSize: (size: AbsoluteNumber) => void;
  onClick: () => void;
  onFocus: () => void;
  onOutsideClick: () => void;
}

interface UniversalCheckboxFieldState {
  disabled: boolean;
  isFocusing: boolean;
  showGuideline: boolean;
}

class UniversalCheckboxField extends Component<
  UniversalCheckboxFieldProps,
  UniversalCheckboxFieldState
> {
  private pointDelta: PointDelta;

  constructor(props: UniversalCheckboxFieldProps) {
    super(props);

    this.state = {
      disabled: false,
      isFocusing: false,
      showGuideline: false,
    };

    this.pointDelta = new PointDelta();

    this.handleDragStart = this.handleDragStart.bind(this);
    this.handleDrag = this.handleDrag.bind(this);
    this.handleDragStop = this.handleDragStop.bind(this);
    this.handleResizeStart = this.handleResizeStart.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.handleResizeStop = this.handleResizeStop.bind(this);
    this.handleFocus = this.handleFocus.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.handleCheckChange = this.handleCheckChange.bind(this);
  }

  handleDragStart(point: AbsolutePoint) {
    this.pointDelta.initializeStartPoint(point);

    this.setState({ showGuideline: true });
  }

  handleDrag(newPoint: AbsolutePoint) {
    const { onChangePoint } = this.props;
    if (this.pointDelta.isExceedThreshold(newPoint)) {
      onChangePoint(newPoint);
    }
  }

  handleDragStop(point: AbsolutePoint) {
    // 드래그를 완료하고 나면 체크가 토글되는 문제가 있어 일정  중에는 input을 disabled 시킵니다.
    this.setState({ disabled: this.pointDelta.isExceedThreshold(point), showGuideline: false });
  }

  handleFocus() {
    const { onFocus } = this.props;
    this.setState({ isFocusing: true });
    onFocus();
  }

  handleBlur() {
    const { onBlur } = this.props;
    this.setState({ isFocusing: false });
    onBlur();
  }

  handleResize(newSize: AbsoluteSize) {
    const { onChangeSize } = this.props;
    onChangeSize(createAbsoluteNumber(newSize.width));
  }

  handleResizeStart() {
    this.setState({ showGuideline: true });
  }

  handleResizeStop() {
    this.setState({ showGuideline: false });
  }

  handleCheckChange(e: ChangeEvent<HTMLInputElement>) {
    const { onChangeCheck } = this.props;
    onChangeCheck(e.target.checked);
  }

  getMaxConstraints(): [number, number] | undefined {
    const {
      point: { x, y },
      viewport: { width, height },
    } = this.props;
    const minSize = Math.min(width - x, height - y);

    return [minSize, minSize];
  }

  render() {
    const {
      point,
      check,
      size,
      dragCancelSelectors,
      rootClassName = '',
      boxClassName = '',
      boxStyle = {},
      draggable = true,
      checkable = true,
      resizable = true,
      checkColor = '#000',
      checkOpacity,
      children,
      onClick,
      onOutsideClick,
      viewport,
    } = this.props;
    const { disabled, isFocusing, showGuideline } = this.state;

    return (
      <FieldBase
        defaultClassName={cx({
          UniversalCheckboxField: true,
          'UniversalCheckboxField--isFocusing': isFocusing,
          [rootClassName]: rootClassName,
        })}
        disabled={!draggable}
        dragCancelSelectors={dragCancelSelectors}
        draggableOpts={{ disabled: !resizable }}
        enableUserSelectHack={false}
        fieldGuideline={{
          viewport,
          guideLineOffset: {
            left: 3,
            top: 2,
            right: 1,
            bottom: 1,
          },
          showGuideLine: showGuideline,
        }}
        lockAspectRatio
        maxConstraints={this.getMaxConstraints()}
        minConstraints={[1, 1]}
        point={point}
        readOnly={!resizable}
        size={createAbsoluteSize({ width: size, height: size })}
        onPointChange={this.handleDrag}
        onPointChangeStart={this.handleDragStart}
        onPointChangeStop={this.handleDragStop}
        onSizeChange={this.handleResize}
        onSizeChangeStart={this.handleResizeStart}
        onSizeChangeStop={this.handleResizeStop}
      >
        <Box height="100%" lineHeight={0} width="100%">
          <OutsideClickHandler onOutsideClick={onOutsideClick}>
            <Box as="label" height="100%" left={0} position="absolute" top={0} width="100%">
              <input
                checked={check}
                disabled={disabled || !checkable}
                style={{
                  height: '100%',
                  width: '100%',
                  overflow: 'hidden',
                  opacity: 0,
                  position: 'fixed',
                  left: '-9999px',
                }}
                type="checkbox"
                onBlur={this.handleBlur}
                onChange={this.handleCheckChange}
                onFocus={this.handleFocus}
              />
              <Box
                className={cx('UniversalCheckboxField__custom-checkbox', boxClassName)}
                style={boxStyle}
                onClick={onClick}
              >
                <CheckIcon
                  color={checkColor}
                  height="100%"
                  opacity={checkOpacity || check ? 1 : 0.1}
                  width="100%"
                />
              </Box>
            </Box>
            {children}
          </OutsideClickHandler>
        </Box>
      </FieldBase>
    );
  }
}

export default UniversalCheckboxField;
